/**
 * @file PrescriptionsPanel.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-14
 * @modified 2026-10-08
 * @description Contact-modal panel: one customer's medicated feed prescriptions,
 *              as a reduced, read-only version of the Prescriptions table.
 *
 *              Reduced to what matters once you are already looking at the
 *              customer: the customer column goes, and the vet reference moves
 *              into the prescription number's tooltip. Read-only — there are no
 *              row actions. A row opens the prescription in the shared modal,
 *              which is where editing lives, so this panel and the Prescriptions
 *              screen offer exactly the same edit path.
 *
 *              The contact modal owns the fetch rather than this panel, because
 *              its nav button shows how many prescriptions are in use before the
 *              panel is ever opened. Mirrors the StatementsPanel and
 *              PaymentBehaviourPanel shape otherwise.
 */

import React, { useState } from 'react';
import PrescriptionModal from './PrescriptionModal';
import { formatDateShort } from '../utils/dateFormatters';
import {
  prescriptionStatusDisplay,
  isPrescriptionInUse,
  isOverPrescribed,
  overPrescribedRowClass,
  overPrescribedTitle,
} from '../utils/prescriptions';
import './Prescriptions.css';
import './PrescriptionsPanel.css';
import { formatQuantity } from '../utils/numberFormatters';

const tonnes = (v) => `${formatQuantity(parseFloat(v || 0))} T`;

/**
 * @param {Array}    prescriptions  This customer's prescriptions, archived included.
 * @param {boolean}  loading
 * @param {string?}  error
 * @param {function} onChanged      Called after a prescription is edited, to refetch.
 */
function PrescriptionsPanel({ prescriptions = [], loading = false, error = null, onChanged }) {
  const [viewing, setViewing] = useState(null);

  const inUse = prescriptions.filter(isPrescriptionInUse).length;

  return (
    <div className="rx-contact-panel">
      <div className="panel-toolbar">
        <div className="toolbar-stats">
          {prescriptions.length} prescription{prescriptions.length !== 1 ? 's' : ''}
          {prescriptions.length > 0 && <> · {inUse} valid</>}
        </div>
      </div>

      {loading ? (
        <div className="rxp-info">Loading…</div>
      ) : error ? (
        <div className="rxp-info rxp-error">{error}</div>
      ) : prescriptions.length === 0 ? (
        <div className="rxp-info">No prescriptions for this customer.</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Rx No.</th>
                <th className="text-right">Tonnes</th>
                <th>Period</th>
                <th className="status-col">Status</th>
              </tr>
            </thead>
            <tbody>
              {prescriptions.map((rx) => {
                const status = prescriptionStatusDisplay(rx);
                const over = isOverPrescribed(rx);
                const rowClasses = [
                  'rx-row',
                  rx.is_archived ? 'inactive-row' : '',
                  overPrescribedRowClass(rx),
                ].filter(Boolean).join(' ');
                return (
                  <tr key={rx.id} className={rowClasses} onClick={() => setViewing(rx)} title="View prescription">
                    <td>
                      <div>{rx.product_name}</div>
                      {rx.product_code && <div className="product-code">{rx.product_code}</div>}
                    </td>
                    <td title={rx.vet_reference ? `Vet ref ${rx.vet_reference}` : undefined}>
                      {rx.prescription_number}
                    </td>
                    <td className="text-right">
                      <div className="tonnes-cell">
                        {over && (
                          <span className="material-symbols-rounded over-prescribed-icon" title={overPrescribedTitle(rx)}>
                            warning
                          </span>
                        )}
                        <span>{tonnes(rx.prescribed_tonnes)}</span>
                      </div>
                      <div className="remaining-tonnes">({tonnes(rx.remaining_tonnes)} left)</div>
                    </td>
                    <td className="rxp-period">
                      <div>{formatDateShort(rx.from_date)}</div>
                      <div className="rxp-muted">to {formatDateShort(rx.to_date)}</div>
                    </td>
                    <td className="status-col">
                      <span className={`status-badge ${status.className}`}>{status.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {viewing && (
        <PrescriptionModal
          prescription={viewing}
          mode="view"
          onClose={() => setViewing(null)}
          onSave={() => { setViewing(null); onChanged?.(); }}
        />
      )}
    </div>
  );
}

export default PrescriptionsPanel;
